import logger from '../../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import '../../css/Signup.css';

import CustomAlert from '../../components/CustomAlert';
import { CurrentUser, signup, fetchCmsLinks } from '../../api/api';
import { setUser } from '../../redux/userSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '../../hooks/useTranslation';
import Footer from '../../components/Footer';

const Signup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslation();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '', // ✅ Add this
    role: '',
    termsAccepted: false,
    marketingOptIn: false,
  });

  const lang = useSelector((state) => state.user.lang) || 'EN';
  const [cmsLinks, setCmsLinks] = useState({});

  useEffect(() => {
    fetchCmsLinks(lang)
      .then((links) => {
        setCmsLinks(links);
      })
      .catch((err) => {
        logger.error('Failed to load CMS links:', err);
      });
  }, [lang]);

  const termsUrl = cmsLinks.terms?.url;
  const privacyUrl = cmsLinks.privacy?.url;

  let termsText = t('registration_terms_privacy_checkbox');

  termsText = termsText.replace('{terms_url}', termsUrl).replace('{privacy_url}', privacyUrl);

  let marketingText = t('registration_marketing_checkbox');
  let validationErrorText = t('registration_terms_privacy_required');

  const getQueryParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      firstName: searchParams.get('first_name') || '',
      lastName: searchParams.get('last_name') || '',
      email: searchParams.get('email') || '',
      role: searchParams.get('role') || '',
    };
  }, [location.search]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      ...getQueryParams(),
    }));
  }, [getQueryParams]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName) {
      CustomAlert.error(t('required_field'), t('first_name_is_required'));
      return;
    }
    if (!formData.lastName) {
      CustomAlert.error(t('required_field'), t('last_name_is_required'));
      return;
    }
    if (!formData.password) {
      CustomAlert.error(t('required_field'), t('password_is_required'));
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      CustomAlert.error(t('required_field'), t('passwords_do_not_match'));
      return;
    }
    if (!formData.termsAccepted) {
      CustomAlert.error(t('required_field'), validationErrorText);
      return;
    }

    try {
      // Yahan aap API call kar sakte hain signup ke liye

      const response = await signup({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.confirmPassword,
        terms_accepted: formData.termsAccepted,
        marketing_opt_in: formData.marketingOptIn,
      });
      // CustomAlert.success("Success", response.message);

      // navigate('/login',{ replace: true });

      // ✅ Get current user
      const userResponse = await CurrentUser();
      dispatch(setUser(userResponse.user || userResponse)); // store in redux

      // ✅ Success
      CustomAlert.success(t('success'), t(response.message));

      // ✅ Go to dashboard directly
      navigate('/dashboard', { replace: true });
    } catch (error) {
      CustomAlert.error(t('Error'), t(error.response?.data?.message || 'signup_failed'));
      logger.error('Signup error:', error);
    }
  };

  return (
    <div
      className="login-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{
          flex: '1 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <div className="login-wrapper">
          <div className="login-image">
            <img src="/img/RocketBPM_rocket_logo.png" alt="Logo" className="login-logo" />
          </div>
          <h2>{t('join_rocketbpmcom')}</h2>
          <form className="login-form" onSubmit={handleSubmit}>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder={t('First_Name')}
              className="login-input"
            />
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder={t('Last_Name')}
              className="login-input"
            />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('Email')}
              className="login-input"
              disabled // Email change na ho
            />

            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t('password')}
              className="login-input"
            />
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('confirm_password')}
              className="login-input"
            />

            {/* Consent Checkboxes */}
            <div className="consent-checkbox-container">
              <label className="consent-checkbox-label">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted || false}
                  onChange={handleCheckboxChange}
                  className="consent-checkbox-input"
                />
                <span
                  className="consent-checkbox-text"
                  dangerouslySetInnerHTML={{ __html: termsText }}
                />
              </label>

              <label className="consent-checkbox-label">
                <input
                  type="checkbox"
                  name="marketingOptIn"
                  checked={formData.marketingOptIn || false}
                  onChange={handleCheckboxChange}
                  className="consent-checkbox-input"
                />
                <span className="consent-checkbox-text">{marketingText}</span>
              </label>
            </div>

            <button type="submit" className="login-button">
              {t('sign_up')}
            </button>
            <p>
              {t('already_a_member')}
              <button className="btn_form" type="button" onClick={() => navigate('/login')}>
                {t('sign_in')}
              </button>
            </p>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Signup;
